import { Outlet, RouteObject } from 'react-router-dom';
import { loadable } from '@sib/ui';
import { paths, ROUTE_SEGMENTS } from '@sidecar/config/paths';

export const routes: RouteObject[] = [
  {
    path: paths.organizations.root,
    element: loadable(() => import('./pages/OrganizationsPage')),
  },
  {
    path: paths.organizations.details.documentsPreview,
    element: loadable(() => import('./pages/OrganizationDocumentPreviewPage')),
  },
  {
    path: paths.organizations.details.documentsViewer,
    element: loadable(() => import('./pages/OrganizationDocumentViewerPage')),
  },
  {
    path: paths.organizations.details.root,
    element: loadable(() => import('./pages/OrganizationDetailsPage')),
    children: [
      {
        path: paths.organizations.details.tab,
        element: <Outlet />,
        children: [
          {
            path: paths.organizations.details.vendorsTab.children.details,
            element: <Outlet />,
            children: [
              {
                path: ROUTE_SEGMENTS.basicInformationEdit,
                element: <Outlet />,
              },
              { path: ROUTE_SEGMENTS.billPayEdit, element: <Outlet /> },
              { path: ROUTE_SEGMENTS.new, element: <Outlet /> },
              { path: ROUTE_SEGMENTS.contactEdit, element: <Outlet /> },
            ],
          },
          {
            path: ROUTE_SEGMENTS.tickets.root,
            element: <Outlet />,
            children: [
              { path: ROUTE_SEGMENTS.new, element: <Outlet /> },
              {
                path: ROUTE_SEGMENTS.tickets.details,
                element: <Outlet />,
                children: [
                  { path: ROUTE_SEGMENTS.edit, element: <Outlet /> },
                  {
                    path: ROUTE_SEGMENTS.tickets.comments,
                    element: <Outlet />,
                  },
                  {
                    path: ROUTE_SEGMENTS.tickets.commentsNew,
                    element: <Outlet />,
                  },
                  {
                    path: ROUTE_SEGMENTS.tickets.relatedTasks,
                    element: <Outlet />,
                  },
                  {
                    path: ROUTE_SEGMENTS.tickets.relatedTasksNew,
                    element: <Outlet />,
                  },
                  {
                    path: ROUTE_SEGMENTS.tickets.activityLog,
                    element: <Outlet />,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTE_SEGMENTS.actionItems.root,
            element: <Outlet />,
            children: [
              {
                path: ROUTE_SEGMENTS.actionItems.details,
                element: <Outlet />,
                children: [{ path: ROUTE_SEGMENTS.edit, element: <Outlet /> }],
              },
            ],
          },
          {
            path: ROUTE_SEGMENTS.users.clientContacts.root,
            element: <Outlet />,
            children: [
              { path: ROUTE_SEGMENTS.new, element: <Outlet /> },
              {
                path: ROUTE_SEGMENTS.users.clientContacts.details,
                element: <Outlet />,
                children: [{ path: ROUTE_SEGMENTS.edit, element: <Outlet /> }],
              },
            ],
          },
          {
            path: ROUTE_SEGMENTS.users.sibEmployees.root,
            element: <Outlet />,
            children: [
              {
                path: ROUTE_SEGMENTS.users.sibEmployees.details,
                element: <Outlet />,
              },
            ],
          },
          { path: ROUTE_SEGMENTS.new, element: <Outlet /> },
          { path: ROUTE_SEGMENTS.locationEdit, element: <Outlet /> },
          { path: ROUTE_SEGMENTS.sibContractId, element: <Outlet /> },
          { path: ROUTE_SEGMENTS.sibContractEdit, element: <Outlet /> },
          { path: ROUTE_SEGMENTS.edit, element: <Outlet /> },
          { path: ROUTE_SEGMENTS.upload, element: <Outlet /> },
          {
            path: ROUTE_SEGMENTS.tasks.root,
            element: <Outlet />,
            children: [
              { path: ROUTE_SEGMENTS.new, element: <Outlet /> },
              {
                path: ROUTE_SEGMENTS.tasks.details,
                element: <Outlet />,
                children: [
                  { path: ROUTE_SEGMENTS.edit, element: <Outlet /> },
                  { path: ROUTE_SEGMENTS.tasks.subtasks, element: <Outlet /> },
                  {
                    path: ROUTE_SEGMENTS.tasks.subtasksNew,
                    element: <Outlet />,
                  },
                  {
                    path: ROUTE_SEGMENTS.tasks.subtasksEdit,
                    element: <Outlet />,
                  },
                  { path: ROUTE_SEGMENTS.tasks.comments, element: <Outlet /> },
                  {
                    path: ROUTE_SEGMENTS.tasks.commentsNew,
                    element: <Outlet />,
                  },
                  {
                    path: ROUTE_SEGMENTS.tasks.activityLog,
                    element: <Outlet />,
                  },
                ],
              },
            ],
          },
          {
            path: ROUTE_SEGMENTS.recurringTasks.root,
            element: <Outlet />,
            children: [
              { path: ROUTE_SEGMENTS.new, element: <Outlet /> },
              {
                path: ROUTE_SEGMENTS.recurringTasks.details,
                element: <Outlet />,
                children: [{ path: ROUTE_SEGMENTS.edit, element: <Outlet /> }],
              },
            ],
          },
          { path: ROUTE_SEGMENTS.emails.root, element: <Outlet /> },
          {
            path: ROUTE_SEGMENTS.vsr.dataEntry.root,
            element: <Outlet />,
          },
          {
            path: ROUTE_SEGMENTS.vsr.reportBuilder.root,
            element: <Outlet />,
          },
        ],
      },
    ],
  },
  {
    path: paths.organizations.details.auditDetails,
    element: loadable(
      () => import('./pages/AuditDetailsPage/AuditDetailsPage')
    ),
    children: [
      {
        path: ROUTE_SEGMENTS.audits.documentsAttach,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditUploadDocuments/routes/AuditDocumentsAttachRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.vendorAccountsAttach,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditSpendEstimated/VendorAccounts/routes/AuditVendorAccountsAttachRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.vendorAccountsNew,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditSpendEstimated/VendorAccounts/routes/AuditVendorAccountsNewRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.spendsNew,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditSpendEstimated/routes/AuditSpendEstimatedSpendsNewRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.spendsImport,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditSpendEstimated/routes/AuditSpendEstimatedSpendsImportRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.negotiationsVendorsAttach,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditNegotiations/routes/AuditNegotiationsVendorsAttachRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.initialValidationSpendsNew,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditInitialValidation/AuditInitialValidationSpendItems/routes/AuditInitialValidationSpendsNewRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.invoicesNew,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditVendorInvoices/InvoiceFormDialog/routes/AuditVendorInvoicesNewRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.invoicesEdit,
        element: loadable(
          () =>
            import(
              './components/AuditDetails/AuditVendorInvoices/InvoiceFormDialog/routes/AuditVendorInvoicesEditRoute'
            )
        ),
      },
      {
        path: ROUTE_SEGMENTS.audits.tab,
        element: <Outlet />,
      },
    ],
  },
];
